import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Plus, Trash2, Edit, ExternalLink, Eye, Users, Database, Shield, ChevronDown, ChevronRight, Building, Send, Mail, FileText, Loader2, CheckCircle, BarChart3, Clock, MessageSquare, DollarSign, AlertTriangle, Bell } from 'lucide-react';
import AdminCredentialVault from './AdminCredentialVault';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminClientManager() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [expandedVault, setExpandedVault] = useState(null);
  const [expandedOnboarding, setExpandedOnboarding] = useState(null);
  const [expandedDigest, setExpandedDigest] = useState(null);
  const [expandedSmsBilling, setExpandedSmsBilling] = useState(null);
  const [form, setForm] = useState({ business_name: '', email: '', password: '', phone: '', address: '', website: '', package: 'starter' });

  const getHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('admin_token')}`,
    'Content-Type': 'application/json',
  });

  const fetchClients = () => {
    fetch(`${API}/api/admin/dashboard-clients`, { headers: getHeaders() })
      .then(r => r.json())
      .then(d => setClients(d.clients || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchClients(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const url = editing
      ? `${API}/api/admin/dashboard-clients/${editing}`
      : `${API}/api/admin/dashboard-clients`;
    const method = editing ? 'PUT' : 'POST';

    const res = await fetch(url, { method, headers: getHeaders(), body: JSON.stringify(form) });
    if (res.ok) {
      setShowForm(false);
      setEditing(null);
      setForm({ business_name: '', email: '', password: '', phone: '', address: '', website: '', package: 'starter' });
      fetchClients();
    } else {
      const err = await res.json();
      alert(err.detail || 'Error');
    }
  };

  const handleDelete = async (slug) => {
    if (!window.confirm('Delete this client and all their data?')) return;
    await fetch(`${API}/api/admin/dashboard-clients/${slug}`, { method: 'DELETE', headers: getHeaders() });
    fetchClients();
  };

  const handleSeedDemo = async (slug) => {
    const res = await fetch(`${API}/api/admin/dashboard-clients/${slug}/seed-demo`, { method: 'POST', headers: getHeaders() });
    const d = await res.json();
    alert(d.message || 'Done');
  };

  const startEdit = (c) => {
    setEditing(c.slug);
    setForm({ business_name: c.business_name, email: c.email, password: '', phone: c.phone || '', address: c.address || '', website: c.website || '', package: c.package || 'starter' });
    setShowForm(true);
  };

  const handleViewDashboard = async (slug) => {
    try {
      const res = await fetch(`${API}/api/admin/dashboard-clients/${slug}/impersonate`, {
        method: 'POST',
        headers: getHeaders(),
      });
      if (!res.ok) {
        const err = await res.json();
        alert(err.detail || 'Failed to impersonate');
        return;
      }
      const data = await res.json();
      localStorage.setItem('client_token', data.access_token);
      localStorage.setItem('client_data', JSON.stringify(data.client));
      window.open('/client/dashboard', '_blank');
    } catch (e) {
      alert('Failed to open client dashboard');
    }
  };

  const handleResendWelcome = async (slug, name) => {
    if (!window.confirm(`Resend welcome email to ${name}? This will generate a new password.`)) return;
    try {
      const res = await fetch(`${API}/api/admin/clients/${slug}/resend-welcome`, { method: 'POST', headers: getHeaders() });
      const d = await res.json();
      if (d.success) {
        alert(`Email ${d.email_sent ? 'sent' : 'FAILED (check SMTP2GO)'}!\n\nNew Password: ${d.temp_password}\nAccess Code: ${d.access_code || 'N/A'}\nOnboarding: ${d.onboarding_url || 'N/A'}`);
      } else {
        alert(d.detail || 'Failed');
      }
    } catch (e) {
      alert('Failed to resend welcome email');
    }
  };

  if (loading) return <div className="text-gray-600 animate-pulse py-8 text-center">Loading clients...</div>;

  return (
    <div className="space-y-6" data-testid="admin-client-manager">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Client Dashboards</h2>
          <p className="text-sm text-gray-600">{clients.length} client{clients.length !== 1 ? 's' : ''} managed</p>
        </div>
        <Button onClick={() => { setShowForm(!showForm); setEditing(null); setForm({ business_name: '', email: '', password: '', phone: '', address: '', website: '', package: 'starter' }); }} className="bg-blue-600 hover:bg-blue-700" data-testid="add-client-btn">
          <Plus className="h-4 w-4 mr-1" /> Add Client
        </Button>
      </div>

      {showForm && (
        <Card className="bg-white border-gray-200">
          <CardHeader><CardTitle className="text-gray-900 text-base">{editing ? 'Edit' : 'New'} Client</CardTitle></CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><Label className="text-gray-600 text-xs">Business Name *</Label><Input value={form.business_name} onChange={e => setForm({...form, business_name: e.target.value})} required className="bg-gray-50 border-gray-200 text-gray-900 mt-1" data-testid="form-business-name" /></div>
              <div><Label className="text-gray-600 text-xs">Email *</Label><Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} required className="bg-gray-50 border-gray-200 text-gray-900 mt-1" data-testid="form-email" /></div>
              <div><Label className="text-gray-600 text-xs">Password {editing ? '(leave blank to keep)' : '*'}</Label><Input type="password" value={form.password} onChange={e => setForm({...form, password: e.target.value})} required={!editing} className="bg-gray-50 border-gray-200 text-gray-900 mt-1" data-testid="form-password" /></div>
              <div><Label className="text-gray-600 text-xs">Phone</Label><Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} className="bg-gray-50 border-gray-200 text-gray-900 mt-1" /></div>
              <div className="md:col-span-2"><Label className="text-gray-600 text-xs">Address</Label><Input value={form.address} onChange={e => setForm({...form, address: e.target.value})} className="bg-gray-50 border-gray-200 text-gray-900 mt-1" /></div>
              <div><Label className="text-gray-600 text-xs">Website</Label><Input value={form.website} onChange={e => setForm({...form, website: e.target.value})} className="bg-gray-50 border-gray-200 text-gray-900 mt-1" /></div>
              <div>
                <Label className="text-gray-600 text-xs">Package</Label>
                <select value={form.package} onChange={e => setForm({...form, package: e.target.value})} className="w-full mt-1 bg-gray-50 border border-gray-200 text-gray-900 rounded-md px-3 py-2 text-sm">
                  <option value="starter">Starter</option>
                  <option value="professional">Professional</option>
                  <option value="enterprise">Enterprise</option>
                </select>
              </div>
              <div className="md:col-span-2 flex gap-2">
                <Button type="submit" className="bg-blue-600 hover:bg-blue-700" data-testid="form-submit-btn">{editing ? 'Update' : 'Create'} Client</Button>
                <Button type="button" variant="outline" onClick={() => { setShowForm(false); setEditing(null); }} className="border-gray-200 text-gray-600">Cancel</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Clients List */}
      <div className="grid gap-4">
        {clients.map(c => (
          <Card key={c.slug} className="bg-white border-gray-200" data-testid={`client-card-${c.slug}`}>
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-10 h-10 rounded-xl bg-blue-600/20 flex items-center justify-center text-blue-600 font-bold text-sm flex-shrink-0">
                    {c.business_name?.charAt(0).toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <p className="font-medium text-gray-900 text-sm truncate">{c.business_name}</p>
                    <p className="text-xs text-gray-500">{c.email} &bull; {c.slug}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge className={`text-[10px] ${c.active ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-600'}`}>
                    {c.active ? 'Active' : 'Inactive'}
                  </Badge>
                  <Badge className="bg-blue-50 text-blue-600 text-[10px] capitalize">{c.package}</Badge>
                  <Button size="sm" variant="ghost" className="h-7 text-gray-600 hover:text-gray-900" onClick={() => handleSeedDemo(c.slug)} title="Seed demo data" data-testid={`seed-${c.slug}`}>
                    <Database className="h-3.5 w-3.5" />
                  </Button>
                  <Button size="sm" variant="ghost" className="h-7 text-gray-600 hover:text-gray-900" onClick={() => startEdit(c)} data-testid={`edit-${c.slug}`}>
                    <Edit className="h-3.5 w-3.5" />
                  </Button>
                  <Button size="sm" variant="ghost" className="h-7 text-amber-600 hover:text-amber-600" onClick={() => setExpandedVault(expandedVault === c.slug ? null : c.slug)} title="Credential Vault" data-testid={`vault-${c.slug}`}>
                    <Shield className="h-3.5 w-3.5" />
                    {expandedVault === c.slug ? <ChevronDown className="h-3 w-3 ml-0.5" /> : <ChevronRight className="h-3 w-3 ml-0.5" />}
                  </Button>
                  <Button size="sm" variant="ghost" className="h-7 text-cyan-600 hover:text-cyan-700" onClick={() => setExpandedOnboarding(expandedOnboarding === c.slug ? null : c.slug)} title="Onboarding Invite" data-testid={`onboarding-${c.slug}`}>
                    <FileText className="h-3.5 w-3.5" />
                  </Button>
                  <Button size="sm" variant="ghost" className="h-7 text-purple-600 hover:text-purple-700" onClick={() => setExpandedDigest(expandedDigest === c.slug ? null : c.slug)} title="Performance Digest" data-testid={`digest-${c.slug}`}>
                    <BarChart3 className="h-3.5 w-3.5" />
                    {expandedDigest === c.slug ? <ChevronDown className="h-3 w-3 ml-0.5" /> : <ChevronRight className="h-3 w-3 ml-0.5" />}
                  </Button>
                  <Button size="sm" variant="ghost" className="h-7 text-green-600/70 hover:text-green-700" onClick={() => setExpandedSmsBilling(expandedSmsBilling === c.slug ? null : c.slug)} title="SMS Credits" data-testid={`sms-billing-${c.slug}`}>
                    <MessageSquare className="h-3.5 w-3.5" />
                    {expandedSmsBilling === c.slug ? <ChevronDown className="h-3 w-3 ml-0.5" /> : <ChevronRight className="h-3 w-3 ml-0.5" />}
                  </Button>
                  <Button size="sm" variant="ghost" className="h-7 text-gray-600 hover:text-red-600" onClick={() => handleDelete(c.slug)} data-testid={`delete-${c.slug}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                  <Button size="sm" variant="ghost" className="h-7 text-emerald-600 hover:text-emerald-700" onClick={() => handleViewDashboard(c.slug)} title="View client dashboard" data-testid={`view-dashboard-${c.slug}`}>
                    <Eye className="h-3.5 w-3.5 mr-1" /> <span className="text-xs">View Dashboard</span>
                  </Button>
                  <Button size="sm" variant="ghost" className="h-7 text-sky-600 hover:text-sky-700" onClick={() => handleResendWelcome(c.slug, c.business_name)} title="Resend welcome email with new password" data-testid={`resend-welcome-${c.slug}`}>
                    <Mail className="h-3.5 w-3.5 mr-1" /> <span className="text-xs">Resend Welcome</span>
                  </Button>
                </div>
              </div>
              {c.last_login && (
                <p className="text-[10px] text-gray-600 mt-2">Last login: {new Date(c.last_login).toLocaleString()}</p>
              )}
            </CardContent>

            {/* Expandable Credential Vault */}
            {expandedVault === c.slug && (
              <div className="border-t border-gray-200 px-4 py-4">
                <AdminCredentialVault slug={c.slug} clientName={c.business_name} />
              </div>
            )}

            {/* Expandable Onboarding Invite Panel */}
            {expandedOnboarding === c.slug && (
              <div className="border-t border-gray-200 px-4 py-4">
                <OnboardingInvitePanel slug={c.slug} clientName={c.business_name} clientEmail={c.email} />
              </div>
            )}

            {/* Expandable Performance Digest Panel */}
            {expandedDigest === c.slug && (
              <div className="border-t border-gray-200 px-4 py-4">
                <DigestPanel slug={c.slug} clientName={c.business_name} clientEmail={c.email} digestSendToClient={c.digest_send_to_client || false} autoDigest={c.auto_digest || false} onToggle={fetchClients} />
              </div>
            )}

            {/* Expandable SMS Billing Panel */}
            {expandedSmsBilling === c.slug && (
              <div className="border-t border-gray-200 px-4 py-4">
                <SmsBillingPanel slug={c.slug} clientName={c.business_name} />
              </div>
            )}
          </Card>
        ))}
        {clients.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            <Users className="h-12 w-12 mx-auto mb-3 text-gray-700" />
            <p>No clients yet. Click "Add Client" to get started.</p>
          </div>
        )}
      </div>
    </div>
  );
}


function OnboardingInvitePanel({ slug, clientName, clientEmail }) {
  const [sending, setSending] = useState(false);
  const [testSending, setTestSending] = useState(false);
  const [sendTo, setSendTo] = useState(clientEmail || '');
  const [testEmail, setTestEmail] = useState('larry@web-techservices.com');
  const [result, setResult] = useState(null);
  const [invites, setInvites] = useState([]);
  const [sendingReminder, setSendingReminder] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/admin/onboarding/submissions`)
      .then(r => r.json())
      .then(d => {
        const mine = (d.submissions || []).filter(s => s.client_slug === slug);
        setInvites(mine);
      })
      .catch(console.error);
  }, [slug]);

  const sendInvite = async () => {
    setSending(true);
    setResult(null);
    try {
      const res = await fetch(`${API}/api/admin/onboarding/invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ slug, send_email: true, send_to_email: sendTo }),
      });
      const data = await res.json();
      if (data.success) {
        setResult({ type: 'success', msg: `Invite sent! Code: ${data.invite.access_code}`, invite: data.invite });
        setInvites(prev => [data.invite, ...prev]);
      } else {
        setResult({ type: 'error', msg: 'Failed to create invite' });
      }
    } catch (err) {
      setResult({ type: 'error', msg: err.message });
    }
    setSending(false);
  };

  const sendTest = async () => {
    setTestSending(true);
    setResult(null);
    try {
      const res = await fetch(`${API}/api/admin/onboarding/test-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ slug, to_email: testEmail }),
      });
      const data = await res.json();
      setResult({ type: data.success ? 'success' : 'error', msg: data.message || (data.success ? 'Test email sent!' : 'Failed') });
    } catch (err) {
      setResult({ type: 'error', msg: err.message });
    }
    setTestSending(false);
  };

  const sendReminder = async (inviteId) => {
    setSendingReminder(inviteId);
    setResult(null);
    try {
      const res = await fetch(`${API}/api/admin/onboarding/send-reminder/${inviteId}`, { method: 'POST' });
      const data = await res.json();
      if (data.success) {
        setResult({ type: 'success', msg: `Reminder sent to ${data.sent_to}` });
      } else {
        setResult({ type: 'error', msg: data.detail || 'Failed to send reminder' });
      }
    } catch (err) {
      setResult({ type: 'error', msg: err.message });
    }
    setSendingReminder(null);
  };

  return (
    <div className="space-y-4" data-testid={`onboarding-panel-${slug}`}>
      <div className="flex items-center gap-2 mb-3">
        <FileText className="w-4 h-4 text-cyan-400" />
        <h3 className="text-sm font-semibold text-gray-900">Onboarding for {clientName}</h3>
      </div>

      {/* Send Test Email */}
      <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
        <p className="text-xs text-gray-600 mb-2 font-medium">Test Email First</p>
        <div className="flex gap-2">
          <Input
            value={testEmail}
            onChange={e => setTestEmail(e.target.value)}
            placeholder="test@email.com"
            className="bg-gray-100 border-gray-300 text-gray-900 text-sm h-8"
            data-testid={`test-email-input-${slug}`}
          />
          <Button size="sm" onClick={sendTest} disabled={testSending} className="bg-amber-600 hover:bg-amber-700 h-8 px-3 text-xs" data-testid={`send-test-btn-${slug}`}>
            {testSending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Mail className="w-3 h-3 mr-1" />}
            Test
          </Button>
        </div>
      </div>

      {/* Send Real Invite */}
      <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
        <p className="text-xs text-gray-600 mb-2 font-medium">Send Invite to Client</p>
        <div className="flex gap-2">
          <Input
            value={sendTo}
            onChange={e => setSendTo(e.target.value)}
            placeholder="client@email.com"
            className="bg-gray-100 border-gray-300 text-gray-900 text-sm h-8"
            data-testid={`invite-email-input-${slug}`}
          />
          <Button size="sm" onClick={sendInvite} disabled={sending} className="bg-blue-600 hover:bg-blue-700 h-8 px-3 text-xs" data-testid={`send-invite-btn-${slug}`}>
            {sending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3 mr-1" />}
            Send
          </Button>
        </div>
      </div>

      {/* Result */}
      {result && (
        <div className={`p-2 rounded text-xs ${result.type === 'success' ? 'bg-green-50 text-green-600 border border-green-800/50' : 'bg-red-50 text-red-600 border border-red-800/50'}`} data-testid="onboarding-result">
          {result.type === 'success' && <CheckCircle className="w-3 h-3 inline mr-1" />}
          {result.msg}
        </div>
      )}

      {/* Existing Invites with Reminder Buttons */}
      {invites.length > 0 && (
        <div>
          <p className="text-xs text-gray-500 mb-2">Previous Invites</p>
          <div className="space-y-1.5">
            {invites.map((inv, i) => (
              <div key={i} className="flex items-center justify-between bg-gray-50 rounded p-2 text-xs">
                <div className="flex items-center gap-2">
                  <code className="text-blue-600 font-mono">{inv.access_code}</code>
                  <Badge className={`text-[9px] ${inv.status === 'submitted' ? 'bg-green-50 text-green-600' : inv.status === 'in_progress' ? 'bg-blue-50 text-blue-600' : 'bg-yellow-900/30 text-yellow-400'}`}>
                    {inv.status}
                  </Badge>
                  {inv.completion?.percentage != null && (
                    <span className="text-gray-500">{inv.completion.percentage}%</span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-gray-600">{inv.created_at ? new Date(inv.created_at).toLocaleDateString() : ''}</span>
                  {inv.status !== 'submitted' && inv.invite_id && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-6 px-2 text-[10px] text-amber-600 hover:text-amber-300 hover:bg-amber-900/20"
                      onClick={() => sendReminder(inv.invite_id)}
                      disabled={sendingReminder === inv.invite_id}
                      data-testid={`send-reminder-${inv.invite_id}`}
                    >
                      {sendingReminder === inv.invite_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Bell className="w-3 h-3 mr-0.5" />}
                      Remind
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}



function DigestPanel({ slug, clientName, clientEmail, digestSendToClient, autoDigest, onToggle }) {
  const [sending, setSending] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [sendToClient, setSendToClient] = useState(digestSendToClient);
  const [autoEnabled, setAutoEnabled] = useState(autoDigest);
  const [toggling, setToggling] = useState(false);
  const [togglingAuto, setTogglingAuto] = useState(false);
  const [result, setResult] = useState(null);
  const [preview, setPreview] = useState(null);
  const [logs, setLogs] = useState([]);

  useEffect(() => {
    fetch(`${API}/api/admin/digest/${slug}/logs`)
      .then(r => r.json())
      .then(d => setLogs(d.logs || []))
      .catch(console.error);
  }, [slug]);

  const handleToggle = async () => {
    setToggling(true);
    const newVal = !sendToClient;
    try {
      const res = await fetch(`${API}/api/admin/digest/${slug}/toggle`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ send_to_client: newVal }),
      });
      if (res.ok) {
        setSendToClient(newVal);
        onToggle();
      }
    } catch (err) { console.error(err); }
    setToggling(false);
  };

  const handleAutoToggle = async () => {
    setTogglingAuto(true);
    const newVal = !autoEnabled;
    try {
      const res = await fetch(`${API}/api/admin/digest/${slug}/auto`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ auto_digest: newVal }),
      });
      if (res.ok) {
        setAutoEnabled(newVal);
        onToggle();
      }
    } catch (err) { console.error(err); }
    setTogglingAuto(false);
  };

  const handlePreview = async () => {
    setPreviewing(true);
    setPreview(null);
    try {
      const res = await fetch(`${API}/api/admin/digest/${slug}/preview`);
      const data = await res.json();
      setPreview(data);
    } catch (err) { setResult({ type: 'error', msg: err.message }); }
    setPreviewing(false);
  };

  const handleSend = async () => {
    setSending(true);
    setResult(null);
    try {
      const res = await fetch(`${API}/api/admin/digest/${slug}/send`, { method: 'POST' });
      const data = await res.json();
      if (data.success) {
        setResult({ type: 'success', msg: `Sent to ${data.sent_to}` });
        // Refresh logs
        const logsRes = await fetch(`${API}/api/admin/digest/${slug}/logs`);
        const logsData = await logsRes.json();
        setLogs(logsData.logs || []);
      } else {
        setResult({ type: 'error', msg: 'Failed to send digest' });
      }
    } catch (err) { setResult({ type: 'error', msg: err.message }); }
    setSending(false);
  };

  return (
    <div className="space-y-4" data-testid={`digest-panel-${slug}`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-900 flex items-center gap-2">
            <BarChart3 className="h-4 w-4 text-purple-400" /> Performance Digest
          </p>
          <p className="text-xs text-gray-500 mt-0.5">Send weekly performance summary for {clientName}</p>
        </div>
      </div>

      {/* Toggle: Admin vs Client */}
      <div className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
        <div>
          <p className="text-xs font-medium text-gray-600">Send digest to:</p>
          <p className="text-[11px] text-gray-500 mt-0.5">
            {sendToClient
              ? <span>Client email (<span className="text-blue-600">{clientEmail}</span>)</span>
              : <span>Admin for review first</span>}
          </p>
        </div>
        <button
          onClick={handleToggle}
          disabled={toggling}
          data-testid={`digest-toggle-${slug}`}
          className={`relative w-14 h-7 rounded-full transition-colors ${sendToClient ? 'bg-blue-600' : 'bg-gray-600'} ${toggling ? 'opacity-50' : ''}`}
        >
          <div className={`absolute top-1 w-5 h-5 bg-white rounded-full transition-transform shadow ${sendToClient ? 'translate-x-7' : 'translate-x-1'}`} />
          <span className="sr-only">Toggle recipient</span>
        </button>
      </div>
      <div className="flex items-center gap-2 text-[10px]">
        <span className={`px-2 py-0.5 rounded-full font-medium ${!sendToClient ? 'bg-amber-50 text-amber-600' : 'bg-gray-100 text-gray-500'}`}>Admin</span>
        <span className="text-gray-600">/</span>
        <span className={`px-2 py-0.5 rounded-full font-medium ${sendToClient ? 'bg-blue-50 text-blue-600' : 'bg-gray-100 text-gray-500'}`}>Client</span>
      </div>

      {/* Auto-Digest Schedule Toggle */}
      <div className="flex items-center justify-between bg-gray-50 rounded-lg p-3" data-testid={`auto-digest-row-${slug}`}>
        <div>
          <p className="text-xs font-medium text-gray-600 flex items-center gap-1.5">
            <Clock className="h-3 w-3 text-green-600" /> Auto-Send Weekly
          </p>
          <p className="text-[11px] text-gray-500 mt-0.5">
            {autoEnabled
              ? <span className="text-green-600">Active — sends every Monday 8:00 AM UTC</span>
              : <span>Disabled — manual send only</span>}
          </p>
        </div>
        <button
          onClick={handleAutoToggle}
          disabled={togglingAuto}
          data-testid={`auto-digest-toggle-${slug}`}
          className={`relative w-14 h-7 rounded-full transition-colors ${autoEnabled ? 'bg-green-600' : 'bg-gray-600'} ${togglingAuto ? 'opacity-50' : ''}`}
        >
          <div className={`absolute top-1 w-5 h-5 bg-white rounded-full transition-transform shadow ${autoEnabled ? 'translate-x-7' : 'translate-x-1'}`} />
          <span className="sr-only">Toggle auto digest</span>
        </button>
      </div>

      {/* Actions */}
      <div className="flex gap-2">
        <Button size="sm" variant="outline" className="text-xs border-gray-200 text-gray-600 hover:text-gray-900" onClick={handlePreview} disabled={previewing} data-testid={`digest-preview-${slug}`}>
          {previewing ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Eye className="h-3 w-3 mr-1" />}
          Preview
        </Button>
        <Button size="sm" className="text-xs bg-purple-600 hover:bg-purple-500 text-gray-900" onClick={handleSend} disabled={sending} data-testid={`digest-send-${slug}`}>
          {sending ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Send className="h-3 w-3 mr-1" />}
          Send Digest
        </Button>
      </div>

      {/* Result */}
      {result && (
        <div className={`text-xs px-3 py-2 rounded ${result.type === 'success' ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-600'}`} data-testid={`digest-result-${slug}`}>
          {result.type === 'success' ? <CheckCircle className="h-3 w-3 inline mr-1" /> : null}
          {result.msg}
        </div>
      )}

      {/* Preview */}
      {preview && (
        <div className="border border-gray-200 rounded-lg overflow-hidden" data-testid={`digest-preview-content-${slug}`}>
          <div className="bg-gray-100 px-3 py-2 flex items-center justify-between">
            <span className="text-[10px] text-gray-600 uppercase tracking-wider">Email Preview</span>
            <button onClick={() => setPreview(null)} className="text-gray-500 hover:text-gray-800 text-xs">&times;</button>
          </div>
          <div className="bg-white p-4 max-h-96 overflow-y-auto" dangerouslySetInnerHTML={{ __html: preview.html }} />
        </div>
      )}

      {/* Send History */}
      {logs.length > 0 && (
        <div>
          <p className="text-xs text-gray-500 mb-2">Recent Sends</p>
          <div className="space-y-1.5">
            {logs.slice(0, 5).map((log, i) => (
              <div key={i} className="flex items-center justify-between bg-gray-50 rounded p-2 text-xs">
                <div className="flex items-center gap-2">
                  <Mail className="h-3 w-3 text-gray-500" />
                  <span className="text-gray-600">{log.sent_to}</span>
                  <Badge className={`text-[9px] ${log.success ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-600'}`}>
                    {log.success ? 'Sent' : 'Failed'}
                  </Badge>
                  {log.send_to_client && <Badge className="text-[9px] bg-blue-50 text-blue-600">Client</Badge>}
                </div>
                <span className="text-gray-600">{log.sent_at ? new Date(log.sent_at).toLocaleString() : ''}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}



function SmsBillingPanel({ slug, clientName }) {
  const [billing, setBilling] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rechargeAmount, setRechargeAmount] = useState('');
  const [rechargeNote, setRechargeNote] = useState('');
  const [recharging, setRecharging] = useState(false);
  const [result, setResult] = useState(null);

  const fetchBilling = async () => {
    try {
      const h = { Authorization: `Bearer ${localStorage.getItem('admin_token')}` };
      const [billingRes, txnRes] = await Promise.all([
        fetch(`${API}/api/admin/sms/billing/${slug}`, { headers: h }).then(r => r.json()),
        fetch(`${API}/api/admin/sms/billing/${slug}/transactions?limit=10`, { headers: h }).then(r => r.json()),
      ]);
      setBilling(billingRes.billing);
      setTransactions(txnRes.transactions || []);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  useEffect(() => { fetchBilling(); }, [slug]);

  const handleRecharge = async () => {
    const amount = parseFloat(rechargeAmount);
    if (!amount || amount <= 0) return;
    setRecharging(true);
    setResult(null);
    try {
      const res = await fetch(`${API}/api/admin/sms/billing/${slug}/recharge`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('admin_token')}` },
        body: JSON.stringify({ amount, note: rechargeNote }),
      });
      const data = await res.json();
      if (data.success) {
        setResult({ type: 'success', msg: `$${amount.toFixed(2)} added. New balance: $${data.balance_after.toFixed(2)}` });
        setRechargeAmount('');
        setRechargeNote('');
        fetchBilling();
      } else {
        setResult({ type: 'error', msg: data.error || 'Failed' });
      }
    } catch (err) { setResult({ type: 'error', msg: err.message }); }
    setRecharging(false);
  };

  if (loading) return <p className="text-gray-500 text-sm animate-pulse">Loading SMS billing...</p>;

  const b = billing || {};
  const balanceColor = b.low_balance ? 'text-red-600' : 'text-green-600';

  return (
    <div className="space-y-4" data-testid={`sms-billing-panel-${slug}`}>
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium text-gray-900 flex items-center gap-2">
          <MessageSquare className="h-4 w-4 text-green-600" /> SMS Credits - {clientName}
        </p>
        <span className="text-xs text-gray-500">$0.049/segment</span>
      </div>

      {/* Balance Overview */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-gray-50 rounded-lg p-3 text-center">
          <p className="text-[10px] text-gray-500 uppercase">Balance</p>
          <p className={`text-xl font-bold ${balanceColor}`}>${(b.balance || 0).toFixed(2)}</p>
          {b.low_balance && <p className="text-[9px] text-red-600 mt-0.5">Low balance</p>}
        </div>
        <div className="bg-gray-50 rounded-lg p-3 text-center">
          <p className="text-[10px] text-gray-500 uppercase">SMS Remaining</p>
          <p className="text-xl font-bold text-gray-900">{b.sms_remaining || 0}</p>
        </div>
        <div className="bg-gray-50 rounded-lg p-3 text-center">
          <p className="text-[10px] text-gray-500 uppercase">Total Spent</p>
          <p className="text-xl font-bold text-amber-600">${(b.total_spent || 0).toFixed(2)}</p>
        </div>
        <div className="bg-gray-50 rounded-lg p-3 text-center">
          <p className="text-[10px] text-gray-500 uppercase">Segments Sent</p>
          <p className="text-xl font-bold text-blue-600">{b.segments_sent || 0}</p>
        </div>
      </div>

      {/* Recharge */}
      <div className="bg-gray-50 rounded-lg p-4">
        <p className="text-xs font-medium text-gray-600 mb-3 flex items-center gap-1.5">
          <DollarSign className="h-3 w-3 text-green-600" /> Add Credits
        </p>
        <div className="flex gap-2">
          <div className="flex-1">
            <Input
              type="number" min="1" step="5" placeholder="Amount ($)"
              value={rechargeAmount} onChange={e => setRechargeAmount(e.target.value)}
              className="bg-slate-900 border-gray-200 text-gray-900 text-sm h-9"
              data-testid={`sms-recharge-amount-${slug}`}
            />
          </div>
          <div className="flex-1">
            <Input
              placeholder="Note (optional)"
              value={rechargeNote} onChange={e => setRechargeNote(e.target.value)}
              className="bg-slate-900 border-gray-200 text-gray-900 text-sm h-9"
            />
          </div>
          <Button size="sm" className="bg-green-600 hover:bg-green-500 text-gray-900 h-9 px-4" onClick={handleRecharge} disabled={recharging} data-testid={`sms-recharge-btn-${slug}`}>
            {recharging ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Add'}
          </Button>
        </div>
        <div className="flex gap-2 mt-2">
          {[10, 25, 50, 100].map(amt => (
            <button key={amt} onClick={() => setRechargeAmount(String(amt))} className="px-3 py-1 text-[10px] rounded-full bg-gray-100 text-gray-600 hover:bg-green-50 hover:text-green-600 transition-colors">
              ${amt}
            </button>
          ))}
        </div>
      </div>

      {result && (
        <div className={`text-xs px-3 py-2 rounded ${result.type === 'success' ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-600'}`}>
          {result.type === 'success' ? <CheckCircle className="h-3 w-3 inline mr-1" /> : <AlertTriangle className="h-3 w-3 inline mr-1" />}
          {result.msg}
        </div>
      )}

      {/* Transaction History */}
      {transactions.length > 0 && (
        <div>
          <p className="text-xs text-gray-500 mb-2">Recent Transactions</p>
          <div className="space-y-1">
            {transactions.slice(0, 8).map((t, i) => (
              <div key={i} className="flex items-center justify-between bg-gray-50 rounded p-2 text-xs">
                <div className="flex items-center gap-2">
                  <Badge className={`text-[9px] ${t.type === 'credit' ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-600'}`}>
                    {t.type === 'credit' ? '+' : '-'}${(t.cost || 0).toFixed(3)}
                  </Badge>
                  <span className="text-gray-600 truncate max-w-[200px]">{t.message_preview || t.direction}</span>
                  {t.segments > 0 && <span className="text-gray-600">{t.segments} seg</span>}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-gray-500">${(t.balance_after || 0).toFixed(2)}</span>
                  <span className="text-gray-600">{t.created_at ? new Date(t.created_at).toLocaleDateString() : ''}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
